import * as moment from 'moment';

/**
 * CLIENT-SIDE PREVIEW of the §4 HOSPITAL RESOLUTION CHAIN of `tasks/risk-management.md`.
 *
 * A PURE MODULE ON PURPOSE, exactly like `redact-name.ts`: plain data in, a plain object out, no
 * Angular, no service injection, no HTTP. That is what lets `risk-location-preview.spec.ts` drive
 * every branch in isolation, and what lets a parity harness feed the SAME fixtures to this and to
 * `utils/riskHospitalResolver.js` and diff the two answers.
 *
 * ── WHAT THIS IS NOW: THE PRE-FILLED DEFAULT, NOT A PREDICTION ───────────────────────────
 *
 * It used to be a guess at what the server would decide, reconciled afterwards. Since §12
 * (2026-08-31) the CLIENT decides: what this computes is pre-filled into the add form, the doctor
 * may change it, and whatever stands at submit time is what the server stores. There is nothing
 * left to reconcile, because there is no second opinion to disagree with.
 *
 * `utils/riskHospitalResolver.js` still runs server-side, but ONLY as the fallback for a caller
 * that sends no `facilityChoice` at all. The two implementations therefore no longer race on the
 * same request — one or the other answers, never both.
 *
 * It still matters that this is right: R4 names confirm-before-add as one of three controls on a
 * feature that produces a letterheaded document alleging failures by a named institution. A
 * doctor cannot meaningfully confirm a facility that was wrong before they saw it — a bad default
 * is now shipped rather than corrected.
 *
 * ── DRIFT IS THE ENEMY ───────────────────────────────────────────────────────────────────
 *
 * Two implementations of one rule set diverge unless divergence is made detectable. Three things
 * hold that line, and all three are load-bearing:
 *
 *   1. This file mirrors the server's control flow STATEMENT BY STATEMENT — same branch order,
 *      same fall-through conditions, same tie-breaks, same normalisation. Where the server does
 *      something surprising (D3-1's inpatient-with-NULL-hospital fall-through; both admission
 *      dates having to parse; the whole-day OVERLAP rather than a point test) this does the same
 *      surprising thing, and says in a comment that it is deliberate.
 *   2. It uses THE SAME LIBRARY, moment, with THE SAME CALLS. `client/package.json` and the
 *      server root both resolve moment 2.30.1, so `moment.utc(raw, moment.ISO_8601, true)` here
 *      and there is not "equivalent parsing", it is the same parser. Hand-rolling an ISO reader
 *      to avoid the import would be a second date grammar to keep in step, which is the exact
 *      failure this section is about.
 *   3. Everything it CANNOT know, it declares. `provisional` + `provisionalReason` are the
 *      honesty valve — see PROVISIONAL below. An honest "the server may still find something"
 *      beats a confident wrong facility on a document headed to a hospital.
 *
 * ── WHAT THE CLIENT CAN NOW SEE THAT IT COULD NOT (E3-1) ────────────────────────────────
 *
 * The first cut of this preview reproduced branches 1, 2 and 4 and assumed branch 3 was
 * impossible client-side, because it needs `PatientAdmissionHistory` and a name→`Hospitals`
 * match. Both are in fact already in the browser wherever this dialog opens:
 *
 *   - `hospital.service.ts` `hospitals$` carries the group's hospitals WITH THEIR WARDS nested
 *     (`hospitalController.js:12` includes the `Ward` model; `admit-details.component.ts:158`
 *     reads `selectedHospital.wards`). So hospital AND ward names resolve client-side.
 *   - `patient-admission-history.service.ts` `patientAdmissionRecords$` is patient-scoped and is
 *     already loaded in the patient file — which is where BOTH callers live (E's
 *     `patient-ai-request-detail` and F's `patient-note-detail`).
 *
 * So all four branches now run here for the currently-open patient. What remains genuinely
 * unknowable is listed under PROVISIONAL, and only that.
 *
 * ── PROVISIONAL: THE FOUR THINGS THE CLIENT STILL CANNOT KNOW ───────────────────────────
 *
 *   P1. STALENESS. `patientAdmissionRecords$` was loaded when the patient file opened. An
 *       admission written since — a discharge captured on another workstation while this dialog
 *       sat open — is invisible here and visible to the server. This is why branch 4 stays
 *       provisional even now that branch 3 runs: "no admission covers this date" is only ever a
 *       statement about the records the browser happens to hold.
 *
 *   P2. DEACTIVATED HOSPITALS. `hospitalController.getHospitalsByGroup` filters
 *       `where: { groupId, active: true }`, so `hospitals$` NEVER contains a deactivated
 *       hospital. `matchHospitalByName` on the server queries `where: { groupId }` — ALL of them.
 *       A branch-3 name matching only a deactivated row therefore resolves to an id on the server
 *       and to `hospitalId: null` here. The divergence is real, it is in the SAFE direction (this
 *       shows the name and no id; the server adds an id), and it is declared rather than hidden.
 *
 *   P3. AN APPOINTMENT THE BROWSER DOES NOT HOLD. Branch 2 keys off the `appointmentId` the
 *       caller will POST, mirroring the server's `findByPk`. If that id is not in the
 *       appointments the client was handed, the client cannot dereference it — the server can.
 *
 *   P4. A TIE ON `admissionDate`. The server sorts covering admissions most-recent-first and
 *       takes the head. Two admissions bearing the SAME `admissionDate` leave that sort to
 *       stability over row order — the DB's order there, the service's here — so when tied
 *       records name DIFFERENT hospitals, which one wins is not a shared decision. Rare, but a
 *       coin-flip between two hospital names is precisely what must never be shown as certain.
 *
 * Everything else — every branch, the 48h grace, the format zoo, the trailing-space match, the
 * duplicate-name tie-break — is reproduced exactly and returns `provisional: false`.
 */

/**
 * §8 Q1, mirroring `GRACE_HOURS_AFTER_DISCHARGE` in `utils/riskHospitalResolver.js`.
 *
 * A note dictated the morning after discharge must still resolve to that hospital; one dictated
 * three weeks later must not. Exported so the spec and the parity harness assert against THIS
 * constant rather than a re-typed 48 — a re-typed number is how the two sides drift by one edit.
 */
export const RISK_PREVIEW_GRACE_HOURS_AFTER_DISCHARGE = 48;

/**
 * The formats actually present in `PatientAdmissionHistories`, in the order the server tries them
 * (`ADMISSION_DATE_FORMATS`). STRICT parsing, deliberately.
 *
 * 'MM/DD/YYYY' IS ABSENT ON PURPOSE, and must stay absent. It is indistinguishable from
 * 'DD/MM/YYYY' for the first twelve days of any month, this is a South African codebase, and
 * guessing wrong silently selects a different admission — a six-month error in the window, not a
 * rounding error. 46 real rows are `DD/MM/YYYY`; none is known to be month-first.
 */
const ADMISSION_DATE_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', 'DD-MM-YYYY'];

/** `PatientRiskItem.location` (D6). The discriminator that keeps "in rooms" and "we could not resolve" apart. */
export type RiskPreviewLocation = 'inpatient' | 'procedure' | 'rooms';

/** Which §4 branch fired. Not a column — for logging, for the dialog, and for the post-POST reconciliation. */
export type RiskPreviewResolvedBy = 'inpatient-status' | 'appointment' | 'admission-history' | 'none';

/** Why the answer is not certain. One of the four cases in the PROVISIONAL block above. */
export type RiskPreviewProvisionalReason =
    | 'stale-admission-records'
    | 'hospital-name-unmatched'
    | 'appointment-not-loaded'
    | 'ambiguous-admission';

/**
 * The preview's answer.
 *
 * The FIRST FIVE FIELDS ARE THE SERVER'S CONTRACT, field for field, so a parity harness can diff
 * the two objects directly with no adapter in between. `provisional` / `provisionalReason` are
 * client-only presentation and have no server counterpart — the server is never unsure, it is
 * simply right.
 */
export interface RiskLocationPreview {
    hospitalId: string | null;
    hospitalName: string | null;
    wardName: string | null;
    location: RiskPreviewLocation;
    resolvedBy: RiskPreviewResolvedBy;
    /** True when the server may still answer differently. See the PROVISIONAL block. */
    provisional: boolean;
    /** Which of the four uncertainties applies, so the dialog can say something specific. */
    provisionalReason: RiskPreviewProvisionalReason | null;
}

/** Only the `Patient` fields §4 reads. Everything optional — `patientId` is NULLABLE on the row. */
export interface RiskPreviewPatient {
    id?: string | null;
    groupId?: string | null;
    /** `models/patient.js` — 'opd' | 'inpatient' | 'invite'. */
    status?: string | null;
    hospitalId?: string | null;
    wardId?: string | null;
}

/** A ward as `hospitals$` nests it. */
export interface RiskPreviewWard {
    id?: string | null;
    name?: string | null;
}

/** A hospital as `hospitals$` emits it, wards nested. */
export interface RiskPreviewHospital {
    id?: string | null;
    groupId?: string | null;
    name?: string | null;
    active?: boolean | null;
    createdAt?: string | Date | null;
    wards?: RiskPreviewWard[] | null;
}

/** Only the `PatientAppointment` fields branch 2 reads. */
export interface RiskPreviewAppointment {
    id?: string | null;
    patientId?: string | null;
    /** Set ONLY for procedure appointments — a consultation legitimately leaves this NULL. */
    hospitalId?: string | null;
}

/**
 * A `PatientAdmissionHistory` row as `patientAdmissionRecords$` emits it.
 *
 * `hospital` and `ward` are PLAIN STRINGS, not FKs — written as `hospital.name` at discharge
 * (`controllers/patientController.js:2192`). That is the whole reason branch 3 can only ever
 * yield a name, and the reason D5 puts both `hospitalId` and `hospitalName` on the item.
 * `admissionDate` / `dischargeDate` are `STRING('MAX')`, in the formats above and in one that is
 * not a format at all.
 */
export interface RiskPreviewAdmissionRecord {
    id?: string | null;
    patientId?: string | null;
    groupId?: string | null;
    active?: boolean | null;
    admissionDate?: string | Date | null;
    dischargeDate?: string | Date | null;
    hospital?: string | null;
    ward?: string | null;
}

/** Everything the preview needs, all of it already in the browser where the dialog opens. */
export interface RiskLocationPreviewInput {
    /** The patient the item will be filed against, or null/absent — most facility issues name none. */
    patient?: RiskPreviewPatient | null;
    /** `hospitals$`, wards nested. ACTIVE ONLY, by construction — see P2. */
    hospitals?: RiskPreviewHospital[] | null;
    /**
     * The encounter, for branch 2 — THE SAME ID THE CALLER WILL POST. The server runs branch 2
     * off the `appointmentId` in the request body and nothing else, so a preview that picked its
     * own appointment would be previewing a branch the server is not going to run.
     */
    appointmentId?: string | null;
    /** The appointments the browser holds, searched for `appointmentId` as `findByPk` would. */
    appointments?: RiskPreviewAppointment[] | null;
    /** `patientAdmissionRecords$` for this patient, for branch 3. */
    admissionRecords?: RiskPreviewAdmissionRecord[] | null;
    /**
     * The calendar day AT THE FACILITY.
     *
     * PASS EXACTLY WHAT WILL BE POSTED, and note that the callers POST
     * `moment(dateOfIssue).format('YYYY-MM-DD')` — a LOCAL calendar day. Handing a raw moment
     * here instead would be a real off-by-one and not a pedantic one: for a note created at
     * 01:00 SAST, `moment.utc(theMoment)` lands on the PREVIOUS day, and a one-day shift is
     * exactly what decides whether the 48h grace window covers the issue. `riskPreviewCalendarDay`
     * below is that same `.format('YYYY-MM-DD')` expression, exported so both sides use one rule;
     * this function applies it to whatever it is given.
     */
    dateOfIssue?: unknown;
    /** Tenancy for the branch-3 name match (D5). Defaults to the patient's `groupId`. */
    groupId?: string | null;
    /**
     * Set ONLY by a harness that hands in deactivated hospitals too. `hospitals$` cannot, so the
     * default is `false` and an unmatched branch-3 name is reported as provisional (P2).
     */
    hospitalsIncludeInactive?: boolean;
}

/**
 * Runs the §4 chain over data the browser already holds.
 *
 * Mirrors `resolveRiskLocation` branch for branch. Never throws: the server degrades a failed
 * resolution to branch 4 rather than losing the issue the doctor just confirmed, and a preview
 * that throws would take the confirm dialog down with it.
 */
export function previewRiskLocation(input: RiskLocationPreviewInput | null | undefined): RiskLocationPreview {
    const args: RiskLocationPreviewInput = input || {};

    // Branch 4 is the answer to "we could not resolve anything", including "there is no patient".
    // It is a FIRST-CLASS OUTCOME under D2 — "we're out of 5-0 nylon" in the rooms is exactly what
    // this feature exists to capture — not a failure, and the dialog must not render it as one.
    // It is nonetheless PROVISIONAL here for P1: the browser's admission records may be stale.
    const unresolved: RiskLocationPreview = {
        hospitalId: null,
        hospitalName: null,
        wardName: null,
        location: 'rooms',
        resolvedBy: 'none',
        provisional: true,
        provisionalReason: 'stale-admission-records',
    };

    try {
        const patient = args.patient || null;
        const hospitals = Array.isArray(args.hospitals) ? args.hospitals : [];
        const effectiveGroupId = args.groupId || (patient ? patient.groupId : null) || null;

        // ── BRANCH 1 — currently an inpatient ────────────────────────────────────────────
        //
        // Authoritative when it fires, and the only branch that needs nothing but the patient.
        //
        // D3-1: a patient whose status is 'inpatient' but whose `hospitalId` is NULL is a
        // half-written admission with nothing to snapshot, and FALLS THROUGH to branches 2-4
        // rather than returning `location: 'inpatient'` with no facility on it. Deleting the
        // `patient.hospitalId` test below would re-open that gap silently.
        if (patient && patient.status === 'inpatient' && patient.hospitalId) {
            const hospital = findHospitalById(hospitals, patient.hospitalId);
            const ward = findWardById(hospital, patient.wardId);
            return {
                hospitalId: patient.hospitalId,
                hospitalName: hospital ? normaliseName(hospital.name) : null,
                wardName: ward ? normaliseName(ward.name) : null,
                location: 'inpatient',
                resolvedBy: 'inpatient-status',
                provisional: false,
                provisionalReason: null,
            };
        }

        // ── BRANCH 2 — the encounter's procedure appointment ─────────────────────────────
        //
        // Only when the caller named an encounter, and only for the id it is going to POST.
        //
        // The patient check is not paranoia and is copied from the server verbatim, including its
        // shape: `!patient || !appointment || appointment.patientId === patient.id`. An
        // appointment belonging to somebody else would snapshot the wrong facility onto this
        // patient's row.
        //
        // P3 — an `appointmentId` the browser cannot dereference does NOT short-circuit. The
        // server's own guard is `appointment && appointment.hospitalId && belongsToSubject`, so a
        // `findByPk` miss FALLS THROUGH to branches 3-4 there; returning early here would preview
        // a chain the server does not run. The chain continues and the uncertainty is carried to
        // whatever it lands on, because the server can dereference what this browser cannot.
        let appointmentNotLoaded = false;
        if (args.appointmentId) {
            const appointment = findAppointmentById(args.appointments, args.appointmentId);
            if (!appointment) appointmentNotLoaded = true;
            const belongsToSubject = !patient || !appointment || appointment.patientId === patient.id;
            if (appointment && appointment.hospitalId && belongsToSubject) {
                const hospital = findHospitalById(hospitals, appointment.hospitalId);
                return {
                    hospitalId: appointment.hospitalId,
                    hospitalName: hospital ? normaliseName(hospital.name) : null,
                    // An appointment names no ward — a theatre case is booked to a hospital, not
                    // to a bed. NULL is the honest answer, not ''.
                    wardName: null,
                    location: 'procedure',
                    resolvedBy: 'appointment',
                    provisional: false,
                    provisionalReason: null,
                };
            }
        }

        // ── BRANCH 3 — admission history covering the day ────────────────────────────────
        //
        // THE BRANCH E3-1 EXISTS TO ADD. Everything it needs is already in the patient file.
        if (patient && args.dateOfIssue) {
            // Normalised to the LOCAL calendar day the caller is going to POST, then parsed with
            // the server's own parser — the two steps the request itself goes through, in order.
            const issueStart = parseFlexibleDate(riskPreviewCalendarDay(args.dateOfIssue));
            if (issueStart) {
                // `dateOfIssue` is a CALENDAR DAY (`DATEONLY`), not an instant, so the test is an
                // OVERLAP between the whole issue day and [admission, discharge + 48h] — NOT a
                // point-in-interval test. A patient admitted at 08:07 and an issue dated that same
                // day must match, and a point test against 00:00 would miss it.
                const issueDayStart = issueStart.clone().startOf('day');
                const issueDayEnd = issueStart.clone().endOf('day');

                const records = Array.isArray(args.admissionRecords) ? args.admissionRecords : [];
                const covering: Array<{ record: RiskPreviewAdmissionRecord; admission: moment.Moment }> = [];

                for (const record of records) {
                    if (!record) continue;
                    // The server's `where: { patientId, active: true }`, applied in JS. `active`
                    // is NOT NULL on the model, so a row arriving without it is a NARROWED
                    // PROJECTION, not an inactive row — the D-review's Sequelize `attributes`
                    // trap, where `undefined` and "this patient has none" are indistinguishable.
                    // Undefined is therefore treated as "not told", i.e. not a reason to drop.
                    if (record.active === false) continue;
                    if (patient.id && record.patientId && record.patientId !== patient.id) continue;

                    const admission = parseFlexibleDate(record.admissionDate);
                    const discharge = parseFlexibleDate(record.dischargeDate);
                    // BOTH ends must parse. 'Invalid date' is 33 real rows, and a window built
                    // from the discharge side alone would fabricate an admission that never
                    // happened. Skipped, never half-guessed.
                    if (!admission || !discharge) continue;

                    const windowStart = admission.clone().startOf('day');
                    const windowEnd = discharge.clone().add(RISK_PREVIEW_GRACE_HOURS_AFTER_DISCHARGE, 'hours');
                    if (windowEnd.isBefore(windowStart)) continue;

                    if (issueDayEnd.isSameOrAfter(windowStart) && issueDayStart.isSameOrBefore(windowEnd)) {
                        covering.push({ record, admission });
                    }
                }

                if (covering.length) {
                    // Most recent first: a patient admitted twice inside one window gets the later
                    // admission, which is the one the doctor was standing in.
                    covering.sort((a, b) => b.admission.valueOf() - a.admission.valueOf());
                    const winner = covering[0];
                    const record = winner.record;

                    const hospitalName = normaliseName(record.hospital);
                    const wardName = normaliseName(record.ward);
                    const matchGroupId = effectiveGroupId || record.groupId || null;
                    const matched = matchHospitalByName(hospitals, matchGroupId, hospitalName);

                    // P4 — a tie on `admissionDate` between records naming DIFFERENT hospitals is
                    // decided by sort stability over row order, and the two sides do not share a
                    // row order. Same name on both: nothing to be uncertain about.
                    const tied = covering.filter(candidate => candidate.admission.valueOf() === winner.admission.valueOf());
                    const ambiguous = tied.length > 1
                        && tied.some(candidate => normaliseName(candidate.record.hospital) !== hospitalName);

                    // P2 — an unmatched name may still match a DEACTIVATED hospital, which
                    // `hospitals$` never carries but `matchHospitalByName` on the server does see.
                    const unmatchedAgainstActiveOnly = !matched && !!hospitalName && !args.hospitalsIncludeInactive;

                    // P3 OUTRANKS the rest. If the server can dereference an appointment this
                    // browser cannot, it returns on BRANCH 2 and never reaches branch 3 at all —
                    // so the uncertainty is not "which hospital did this admission name", it is
                    // "the server may not be answering this question at all".
                    const reason: RiskPreviewProvisionalReason | null = appointmentNotLoaded
                        ? 'appointment-not-loaded'
                        : (ambiguous
                            ? 'ambiguous-admission'
                            : (unmatchedAgainstActiveOnly ? 'hospital-name-unmatched' : null));

                    return {
                        // NULL on a miss. NEVER INVENTED (D5) — a wrong `hospitalId` files the row
                        // under a hospital that was never involved, which on a document handed to
                        // a hospital is worse than no id at all. D12 keeps such name-only rows as
                        // their own group in the export rather than merging them into a
                        // similarly-named one.
                        hospitalId: matched && matched.id ? matched.id : null,
                        hospitalName,
                        wardName,
                        location: 'inpatient',
                        resolvedBy: 'admission-history',
                        provisional: reason !== null,
                        provisionalReason: reason,
                    };
                }
            }
        }

        // ── BRANCH 4 — in rooms ─────────────────────────────────────────────────────────
        // Still branch 4, but say WHY it is uncertain: an appointment we could not read is a
        // different kind of "we don't know" from admission records that might be stale.
        return appointmentNotLoaded
            ? { ...unresolved, provisionalReason: 'appointment-not-loaded' }
            : unresolved;
    } catch {
        // The server degrades to branch 4 on any failure rather than losing the issue the doctor
        // has already decided to create; a preview that threw would take the dialog with it.
        return unresolved;
    }
}

/**
 * The `dateOfIssue` EXACTLY AS THE CALLERS POST IT.
 *
 * `patient-ai-request-detail` and `patient-note-detail` both build their payload with
 * `dateOfIssue: moment(result.dateOfIssue).format('YYYY-MM-DD')`. This is that expression, and it
 * exists so the preview and the request cannot disagree about which day is being asked about.
 *
 * LOCAL, not UTC, and deliberately so — because `.format()` is local, and the preview's job is to
 * predict what the server will be told, not to have its own opinion about the day. `moment()`
 * without a format is forgiving here on purpose too: this is a real date object or an ISO string
 * from our own code, not one of `PatientAdmissionHistory`'s hand-typed strings, and the strict
 * grammar belongs on those and only those.
 */
export function riskPreviewCalendarDay(value: unknown): string | null {
    if (!value) return null;

    // The forgiving `moment(...)` below falls back to `new Date(str)` for anything it does not
    // recognise, and SHOUTS a deprecation warning down the console while doing it. The result is
    // still correctly invalid, but a doctor's console filling with moment stack traces every time
    // a bad date passes through is noise that hides real errors — so the known-bad strings are
    // rejected up front, with the same words `parseFlexibleDate` uses.
    if (typeof value === 'string') {
        const raw = value.trim().toLowerCase();
        if (!raw || raw === 'invalid date' || raw === 'null') return null;
    }

    const parsed = moment(value as moment.MomentInput);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

/**
 * Parses one of the string dates on `PatientAdmissionHistory`, or a real `Date`.
 *
 * CHARACTER FOR CHARACTER the server's `parseFlexibleDate` — same guards, same order, same strict
 * flags, same library at the same version (moment 2.30.1 on both sides). Do not "improve" it here
 * alone; a change that is not made in `utils/riskHospitalResolver.js` too is a divergence, and the
 * only thing worse than an unparseable date is two parsers that disagree about which dates those
 * are.
 *
 * Returns a UTC moment or `null`, NEVER an invalid moment, so no caller can propagate
 * `Invalid date` into a comparison that quietly answers false.
 */
export function parseFlexibleDate(value: unknown): moment.Moment | null {
    if (!value) return null;

    if (value instanceof Date) {
        const fromDate = moment.utc(value);
        return fromDate.isValid() ? fromDate : null;
    }

    const raw = String(value).trim();
    // 'Invalid date' is 33 real rows — moment's own toString for an unparseable value, written
    // straight to a STRING column. It carries no information and must not be guessed at.
    if (!raw || raw.toLowerCase() === 'invalid date' || raw.toLowerCase() === 'null') return null;

    const iso = moment.utc(raw, moment.ISO_8601, true);
    if (iso.isValid()) return iso;

    const explicit = moment.utc(raw, ADMISSION_DATE_FORMATS, true);
    return explicit.isValid() ? explicit : null;
}

/**
 * Trims to a real value or `null`. '' and '   ' are not names — the `ward` column contains both,
 * and an empty string rendered into "Where this will be filed" reads as a hospital with no name
 * rather than as no hospital.
 */
export function normaliseName(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : null;
}

/**
 * Best-effort NAME → hospital, WITHIN ONE GROUP (D5). The server's `matchHospitalByName`, over the
 * array `hospitals$` already holds instead of over a query.
 *
 * Case- and whitespace-insensitive; NOTHING CLEVERER, and this must stay that way. A fuzzy match
 * would be actively dangerous: 'Netcare Milpark' and 'Netcare Milpark Hospital' are plausibly the
 * same place, but 'Life Bedford Gardens' and 'Life Bedfordview' are not, and the cost of a wrong
 * match is a complaint filed against a hospital that was never involved. D12 settles this
 * explicitly — DO NOT "fix" the `Olivedale` / `Netcare Olivedale` miss by loosening it.
 *
 * The trim matters on real data: three rows in one group are named `'Akasia '`, with the trailing
 * space, and a raw `===` would fail to match the history's `'Akasia'`.
 *
 * The group scope is a TENANCY boundary, not an optimisation — hospital names repeat across
 * practices. A hospital arriving without a `groupId` is not evidence of another practice's row:
 * `hospitals$` is fetched per group, so the field is simply not always projected, and dropping
 * those would make the match fail for every practice whose payload omits it.
 *
 * DUPLICATES ARE REAL — 3 groups have them. Ties break deterministically, ACTIVE FIRST then
 * OLDEST, so the same input always resolves to the same row rather than to whatever came back
 * first that day.
 */
export function matchHospitalByName(
    hospitals: RiskPreviewHospital[] | null | undefined,
    groupId: string | null | undefined,
    name: string | null | undefined
): RiskPreviewHospital | null {
    if (!groupId || !name) return null;
    const candidates = Array.isArray(hospitals) ? hospitals : [];

    const target = name.trim().toLowerCase();
    const matches = candidates.filter(hospital => {
        if (!hospital) return false;
        if (hospital.groupId && hospital.groupId !== groupId) return false;
        return String(hospital.name || '').trim().toLowerCase() === target;
    });
    if (!matches.length) return null;

    matches.sort((a, b) => {
        const aActive = !!a.active;
        const bActive = !!b.active;
        if (aActive !== bActive) return bActive ? 1 : -1;
        return createdAtValue(a.createdAt) - createdAtValue(b.createdAt);
    });
    return matches[0];
}

/**
 * `new Date(row.createdAt || 0)` from the server's tie-break, as a number.
 *
 * The `|| 0` epoch fallback is copied deliberately: a row WITHOUT a `createdAt` sorts oldest and
 * therefore WINS the tie, on both sides. It looks like a bug and is in fact the only choice that
 * keeps the two implementations agreeing, which is the property that matters here.
 */
function createdAtValue(value: string | Date | null | undefined): number {
    if (!value) return 0;
    // NaN is NOT normalised away. An unparseable `createdAt` makes the server's subtraction NaN,
    // which `Array.prototype.sort` reads as "leave these two alone"; coercing it to 0 here would
    // reorder a pair the server does not reorder. Bug-for-bug is the point.
    return (value instanceof Date ? value : new Date(value)).valueOf();
}

/** `Hospital.findByPk` over the array the browser holds. */
function findHospitalById(hospitals: RiskPreviewHospital[], id: string | null | undefined): RiskPreviewHospital | null {
    if (!id) return null;
    return hospitals.find(hospital => !!hospital && hospital.id === id) || null;
}

/**
 * `Ward.findByPk` over the wards nested inside the hospital.
 *
 * Scoped to the hospital rather than searched across all of them on purpose: a `wardId` that does
 * not belong to `patient.hospitalId` is a data inconsistency, and naming some other hospital's
 * ward on the row would be worse than naming none.
 */
function findWardById(hospital: RiskPreviewHospital | null, wardId: string | null | undefined): RiskPreviewWard | null {
    if (!hospital || !wardId || !Array.isArray(hospital.wards)) return null;
    return hospital.wards.find(ward => !!ward && ward.id === wardId) || null;
}

/** `PatientAppointment.findByPk` over the array the browser holds. */
function findAppointmentById(
    appointments: RiskPreviewAppointment[] | null | undefined,
    id: string
): RiskPreviewAppointment | null {
    if (!Array.isArray(appointments)) return null;
    return appointments.find(appointment => !!appointment && appointment.id === id) || null;
}

/**
 * REMOVED 2026-08-31 (§12): `riskPreviewProvisionalNote` and `riskPreviewDisagreesWithServer`.
 *
 * Both existed to manage a disagreement between this preview and the server resolver. Since the
 * client decides the facility there is no second opinion to disagree with, so both had zero
 * production callers — kept alive only by their own specs, which is worse than deletion: a
 * passing test on unreachable code reads as coverage.
 *
 * The four provisional REASONS are still computed and still on `RiskLocationPreview`. They are
 * genuine facts about how confident the default is, and a future caller may want them; what is
 * gone is only the prose and the comparison built for reconciliation.
 */
